(() => {
  const clamp = (n, min, max) => Math.min(max, Math.max(min, n));
  const storageKey = (handle) => `bhm-reader-progress:${handle}`;
  const DESKTOP_SPREAD_MEDIA = '(min-width: 992px)';
  const SCROLL_OFFSET = 24;
  const DESKTOP_STAGE_OFFSET = 24;
  const DESKTOP_ZOOM_MIN = 1;
  const DESKTOP_ZOOM_MAX = 2.2;
  const DESKTOP_ZOOM_STEP = 0.2;
  const DESKTOP_VIEW_MODE_SPREAD = 'spread';
  const DESKTOP_VIEW_MODE_SINGLE = 'single';
  const DESKTOP_VIEW_MODE_GRID = 'grid';
  const TURN_DURATION_SPREAD = 900;
  const TURN_DURATION_SINGLE = 720;
  // The turning page is cut into strips about this wide (px) so it can bend.
  const TURN_STRIP_WIDTH = 24;
  // How a real page moves: the free edge lifts first while the spine side lies flat, then
  // the spine side sweeps over and the edge trails behind, landing last. Each strip turns
  // within its own slice of the turn; these set how late the spine starts (share of the
  // turn) and how early it lands.
  const TURN_SPINE_LIFT_DELAY = 0.35;
  const TURN_SPINE_LAND_EARLY = 0.25;
  // Viewing distance for the 3D turn, in page widths.
  const TURN_PERSPECTIVE = 4;
  const TURN_FADE_DURATION = 240;
  const TURN_LOAD_TIMEOUT = 500;
  const WHEEL_TURN_THRESHOLD = 40;
  const WHEEL_GESTURE_GAP = 180;
  const PHONE_LAYOUT_KEY = 'bhm-reader-phone-layout';
  const PHONE_LAYOUT_SCROLL = 'scroll';
  const PHONE_LAYOUT_PAGES = 'pages';
  const DRAG_START_DISTANCE = 10;
  // A swipe turns the page once the finger has crossed this share of the page width,
  // or at the flick speed (px/ms) once it has moved at least the flick share.
  const DRAG_COMMIT_DISTANCE = 0.25;
  const DRAG_FLICK_DISTANCE = 0.08;
  const DRAG_FLICK_SPEED = 0.35;
  const TAP_EDGE_FRACTION = 0.35;

  function safeJsonParse(value) {
    try {
      return JSON.parse(value);
    } catch {
      return null;
    }
  }

  function getManifest(manifestId) {
    const el = document.getElementById(manifestId);
    return el ? safeJsonParse(el.textContent) : null;
  }

  function saveProgress(handle, pageIndex, progress) {
    try {
      localStorage.setItem(
        storageKey(handle),
        JSON.stringify({
          pageIndex,
          progress,
          updatedAt: Date.now()
        })
      );
    } catch (_) {}
  }

  function getSavedProgress(handle) {
    try {
      return safeJsonParse(localStorage.getItem(storageKey(handle)));
    } catch (_) {
      return null;
    }
  }

  function clearSavedProgress(handle) {
    try {
      localStorage.removeItem(storageKey(handle));
    } catch (_) {}
  }

  // Phones and tablets scroll by default; a reader who picks Pages keeps it for every title.
  function getPhoneLayout() {
    try {
      return localStorage.getItem(PHONE_LAYOUT_KEY) === PHONE_LAYOUT_PAGES
        ? PHONE_LAYOUT_PAGES
        : PHONE_LAYOUT_SCROLL;
    } catch (_) {
      return PHONE_LAYOUT_SCROLL;
    }
  }

  function savePhoneLayout(layout) {
    try {
      localStorage.setItem(PHONE_LAYOUT_KEY, layout);
    } catch (_) {}
  }

  function createPage(url, index, title) {
    const page = document.createElement('article');
    page.className = 'reader-page';
    page.dataset.pageIndex = String(index);

    const img = document.createElement('img');
    img.alt = `${title} page ${index + 1}`;
    img.loading = 'lazy';
    img.decoding = 'async';
    img.src = url;

    page.appendChild(img);
    return page;
  }

  function createSpread(spreadIndex, spreadPages) {
    const spread = document.createElement('section');
    spread.className = 'reader-spread';
    spread.dataset.spreadIndex = String(spreadIndex);
    spread.dataset.startPageIndex = spreadPages[0]?.dataset.pageIndex || '0';

    if (spreadPages.length === 1) {
      spread.classList.add('reader-spread--single');
    }

    spreadPages.forEach((page) => {
      spread.appendChild(page);
    });

    return spread;
  }

  function createGridItem(url, index, title) {
    const item = document.createElement('button');
    item.type = 'button';
    item.className = 'reader-grid__item';
    item.dataset.pageIndex = String(index);
    item.setAttribute('aria-label', `Open page ${index + 1}`);

    const thumb = document.createElement('div');
    thumb.className = 'reader-grid__thumb';

    const img = document.createElement('img');
    img.alt = `${title} page ${index + 1} thumbnail`;
    img.loading = 'lazy';
    img.decoding = 'async';
    img.src = url;

    const meta = document.createElement('div');
    meta.className = 'reader-grid__meta';

    const number = document.createElement('span');
    number.className = 'reader-grid__page-number';
    number.textContent = `Page ${index + 1}`;

    thumb.appendChild(img);
    meta.appendChild(number);
    item.appendChild(thumb);
    item.appendChild(meta);

    return item;
  }

  function getCurrentScrollPosition(pages) {
    const probeY = window.innerHeight * 0.18;
    let bestIndex = 0;
    let bestScore = Infinity;

    pages.forEach((page, index) => {
      const rect = page.getBoundingClientRect();
      const score = Math.abs(rect.top - probeY);

      if (score < bestScore) {
        bestScore = score;
        bestIndex = index;
      }
    });

    const currentPage = pages[bestIndex];
    const rect = currentPage.getBoundingClientRect();
    const progress = clamp((probeY - rect.top) / Math.max(rect.height, 1), 0, 1);

    return { pageIndex: bestIndex, progress };
  }

  function waitForImage(img) {
    if (!img) return Promise.resolve();

    if (img.loading === 'lazy') {
      img.loading = 'eager';
    }

    if ('fetchPriority' in img) {
      img.fetchPriority = 'high';
    }

    if (img.complete && img.naturalWidth > 0) {
      if (typeof img.decode === 'function') {
        return img.decode().catch(() => {});
      }
      return Promise.resolve();
    }

    return new Promise((resolve) => {
      const done = async () => {
        if (typeof img.decode === 'function') {
          try {
            await img.decode();
          } catch (_) {}
        }
        resolve();
      };

      img.addEventListener('load', done, { once: true });
      img.addEventListener('error', done, { once: true });

      if (img.src) {
        img.src = img.src;
      }
    });
  }

  function primeSpreadImages(spread) {
    if (!spread) return [];

    const images = Array.from(spread.querySelectorAll('img'));
    images.forEach((img) => {
      img.loading = 'eager';
      if ('fetchPriority' in img) {
        img.fetchPriority = 'high';
      }
      if (img.src) {
        img.src = img.src;
      }
    });

    return images;
  }

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function whenImageLoaded(img) {
    if (!img || img.complete) return Promise.resolve();

    return new Promise((resolve) => {
      img.addEventListener('load', resolve, { once: true });
      img.addEventListener('error', resolve, { once: true });
    });
  }

  function waitForSpreadImages(spread, timeout) {
    const images = spread ? Array.from(spread.querySelectorAll('img')) : [];

    return Promise.race([
      Promise.all(images.map(whenImageLoaded)),
      new Promise((resolve) => setTimeout(resolve, timeout))
    ]);
  }

  // Where an object-fit: contain image actually draws inside its box, relative to origin.
  // Left pages hug the spine on their right edge, right pages on their left.
  function getContentRect(img, side, origin) {
    if (!img || !img.naturalWidth || !img.naturalHeight) return null;

    const box = img.getBoundingClientRect();
    if (!box.width || !box.height) return null;

    const scale = Math.min(box.width / img.naturalWidth, box.height / img.naturalHeight);
    const w = img.naturalWidth * scale;
    const h = img.naturalHeight * scale;
    let x = box.left + (box.width - w) / 2;

    if (side === 'left') x = box.right - w;
    if (side === 'right') x = box.left;

    return {
      x: x - origin.left,
      y: box.top + (box.height - h) / 2 - origin.top,
      w,
      h
    };
  }

  function createTurnPiece(className, face, { offsetX = 0, withImage = true } = {}) {
    const piece = document.createElement('div');
    piece.className = className;
    piece.style.left = `${face.rect.x - offsetX}px`;
    piece.style.top = `${face.rect.y}px`;
    piece.style.width = `${face.rect.w}px`;
    piece.style.height = `${face.rect.h}px`;

    if (withImage) {
      const img = document.createElement('img');
      img.alt = '';
      img.src = face.src;
      piece.appendChild(img);
    }

    return piece;
  }

  function createTurnShadow(face, darkSide) {
    const shadow = createTurnPiece('reader-turn__cast', face, { withImage: false });
    const towards = darkSide === 'left' ? 'right' : 'left';
    shadow.style.background = `linear-gradient(to ${towards}, rgba(0, 0, 0, 0.55), rgba(0, 0, 0, 0.18) 30%, rgba(0, 0, 0, 0) 60%)`;
    return shadow;
  }

  const easeInOutSine = (t) => (1 - Math.cos(Math.PI * t)) / 2;
  const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

  // How far (0..1) the strip at `position` along the page (0 = spine, 1 = free edge) has
  // turned when the whole turn is at `progress`.
  function stripTurn(progress, position) {
    const start = (1 - position) * TURN_SPINE_LIFT_DELAY;
    const end = 1 - (1 - position) * TURN_SPINE_LAND_EARLY;
    const t = clamp((progress - start) / (end - start), 0, 1);
    // Paper springs up as soon as it is lifted but settles softly: a quick start blended
    // with an even ease, both arriving at rest.
    return 0.45 * easeInOutSine(t) + 0.55 * (1 - (1 - t) * (1 - t));
  }

  // Straight-line interpolation through [progress, value] points.
  function piecewise(points, t) {
    for (let i = 1; i < points.length; i++) {
      const [x1, y1] = points[i];
      const [x0, y0] = points[i - 1];
      if (t <= x1) return y0 + ((y1 - y0) * (t - x0)) / Math.max(x1 - x0, 1e-6);
    }
    return points[points.length - 1][1];
  }

  // One slice of a page's art, cut from the full image painted as a background.
  function paintSlice(el, src, rect, offsetX) {
    el.style.backgroundImage = `url("${String(src).replace(/"/g, '\\"')}")`;
    el.style.backgroundSize = `${rect.w}px ${rect.h}px`;
    el.style.backgroundPosition = `${offsetX}px ${rect.y}px`;
    el.style.backgroundRepeat = 'no-repeat';
  }

  // Plain paper the height of the page, in a colour the turn sets per frame.
  function paintPaper(el, rect) {
    el.style.backgroundImage = 'linear-gradient(var(--reader-turn-wash), var(--reader-turn-wash))';
    el.style.backgroundSize = `100% ${rect.h}px`;
    el.style.backgroundPosition = `0 ${rect.y}px`;
    el.style.backgroundRepeat = 'no-repeat';
  }

  function createSliceShade(rect) {
    const shade = document.createElement('span');
    shade.className = 'reader-turn__shade';
    shade.style.top = `${rect.y}px`;
    shade.style.height = `${rect.h}px`;
    return shade;
  }

  // Plays a page turn over pages that are already in their new place underneath.
  // `from` and `to` are the visible pages before and after, as { src, side, rect }.
  // The turning page is cut into narrow vertical strips chained edge to edge, each turned a
  // little further than the one before it, so the paper bends: the free edge leads and the
  // part at the spine follows. A copy of the page it lands on stays on top of the new pages
  // until it is covered. Frames are computed in script, so a finger can drive the turn:
  // with `scrub` it starts held at 0, setProgress() moves it, and release() completes it or
  // runs it back, in which case `done` resolves 'cancelled'. Returns null when there is
  // nothing to animate.
  function playPageTurn(container, direction, from, to, { scrub = false } = {}) {
    const forward = direction > 0;
    const isPair = (faces) => faces.length === 2 && faces.every((face) => face.rect);
    const isLone = (faces) =>
      faces.length === 1 && faces[0].rect && faces[0].side === 'center';

    let plan = null;

    if (isPair(from) && isPair(to)) {
      plan = forward
        ? { front: from[1], back: to[0], cover: from[0], reveal: to[1] }
        : { front: to[1], back: from[0], cover: from[1], reveal: to[0] };
    } else if (isLone(from) && isLone(to)) {
      plan = forward
        ? { front: from[0], ghost: true, cover: null, reveal: to[0] }
        : { front: to[0], ghost: true, cover: from[0], reveal: null };
    }

    const kind = !plan ? 'fade' : plan.ghost ? 'single' : 'spread';
    const duration =
      kind === 'fade' ? TURN_FADE_DURATION : kind === 'single' ? TURN_DURATION_SINGLE : TURN_DURATION_SPREAD;

    const layer = document.createElement('div');
    layer.className = 'reader-turn';
    layer.setAttribute('aria-hidden', 'true');

    let render;
    let progressForEdge = null;

    if (!plan) {
      // Into or out of a lone last page the geometry changes, so cross-fade instead.
      const faces = from.filter((face) => face.rect);
      if (!faces.length) return null;

      faces.forEach((face) => {
        layer.appendChild(createTurnPiece('reader-turn__cover', face));
      });

      render = (q) => {
        layer.style.opacity = String(1 - q);
      };
    } else {
      const spineX = plan.front.rect.x;
      const pageWidth = plan.front.rect.w;
      const stripCount = clamp(Math.round(pageWidth / TURN_STRIP_WIDTH), 8, 18);
      const stripWidth = pageWidth / stripCount;

      if (plan.cover) {
        layer.appendChild(createTurnPiece('reader-turn__cover', plan.cover));
      }

      const revealShadow = plan.reveal
        ? createTurnShadow(plan.reveal, forward ? 'left' : 'right')
        : null;
      const landShadow = plan.cover ? createTurnShadow(plan.cover, forward ? 'right' : 'left') : null;
      if (revealShadow) layer.appendChild(revealShadow);
      if (landShadow) layer.appendChild(landShadow);

      // Depth in proportion to the page, so a phone-sized page bends as visibly as a desktop one.
      layer.style.perspective = `${Math.round(Math.max(900, pageWidth * TURN_PERSPECTIVE))}px`;
      layer.style.perspectiveOrigin = `${Math.round(plan.ghost ? spineX + pageWidth / 2 : spineX)}px 50%`;

      const sheet = document.createElement('div');
      sheet.className = 'reader-turn__sheet';
      sheet.style.left = `${spineX}px`;
      sheet.style.width = `${pageWidth}px`;
      layer.appendChild(sheet);

      // The back of the sheet: the page it lands as, or (single-page view) the plain reverse
      // of the paper. Strip i lands i strips left of the spine.
      const backPage = plan.ghost ? plan.front : plan.back;
      const strips = [];
      let parent = sheet;

      for (let i = 0; i < stripCount; i++) {
        const strip = document.createElement('div');
        strip.className = 'reader-turn__strip';
        strip.style.left = i === 0 ? '0' : `${stripWidth}px`;
        // One pixel of overlap hides the hairline seams between tilted strips.
        strip.style.width = `${stripWidth + 1}px`;

        const front = document.createElement('div');
        front.className = 'reader-turn__face reader-turn__face--front';
        paintSlice(front, plan.front.src, plan.front.rect, -i * stripWidth);
        const frontShade = createSliceShade(plan.front.rect);
        front.appendChild(frontShade);

        const back = document.createElement('div');
        back.className = 'reader-turn__face reader-turn__face--back';
        if (plan.ghost) {
          paintPaper(back, backPage.rect);
        } else {
          paintSlice(back, backPage.src, backPage.rect, -(backPage.rect.w - (i + 1) * stripWidth));
        }
        const backShade = createSliceShade(backPage.rect);
        back.appendChild(backShade);

        strip.appendChild(front);
        strip.appendChild(back);
        parent.appendChild(strip);
        strips.push({ el: strip, frontShade, backShade, position: (i + 0.5) / stripCount });
        parent = strip;
      }

      const toRadians = Math.PI / 180;

      // Each strip's angle off the right-hand page, 0° (flat on the right) to 180° (flat on
      // the left). A backward turn is the same motion run from the other side.
      const angleAt = (q, position) => {
        const turned = stripTurn(q, position);
        return 180 * (forward ? turned : 1 - turned);
      };
      const stripAngle = (q, strip) => angleAt(q, strip.position);

      // Where the free edge sits (px from the spine, ignoring perspective) at a given progress.
      const edgeX = (q) =>
        strips.reduce((sum, strip) => sum + stripWidth * Math.cos(stripAngle(q, strip) * toRadians), 0);

      // The progress that puts the free edge at `x`: lets a finger hold the edge itself.
      progressForEdge = (x) => {
        let low = 0;
        let high = 1;

        for (let i = 0; i < 22; i++) {
          const mid = (low + high) / 2;
          const reached = forward ? edgeX(mid) <= x : edgeX(mid) >= x;
          if (reached) high = mid;
          else low = mid;
        }

        return (low + high) / 2;
      };

      const tint = (value) =>
        value >= 0
          ? `rgba(255, 255, 255, ${value.toFixed(3)})`
          : `rgba(0, 0, 0, ${(-value).toFixed(3)})`;

      // Light comes from in front: paper turned away from it darkens, and the part of the
      // curve that faces it at a glancing angle catches a soft sheen.
      const lightAt = (angle) => {
        const tilt = angle < 90 ? angle : 180 - angle;
        const shade = (1 - Math.cos(tilt * toRadians)) * 0.6;
        const sheen = 0.09 * Math.exp(-Math.pow((tilt - 32) / 14, 2));
        return tint(sheen - shade);
      };

      render = (q) => {
        let previous = 0;
        // Light is worked out at the strip edges and blended across each strip, so it flows
        // smoothly over the curve instead of stepping from strip to strip.
        const edges = [];
        for (let k = 0; k <= stripCount; k++) edges.push(lightAt(angleAt(q, k / stripCount)));

        strips.forEach((strip, i) => {
          const angle = stripAngle(q, strip);
          strip.el.style.transform = `rotateY(${(previous - angle).toFixed(2)}deg)`;
          previous = angle;

          // The back face is mirrored, so its gradient runs the other way.
          strip.frontShade.style.background =
            angle < 90 ? `linear-gradient(to right, ${edges[i]}, ${edges[i + 1]})` : 'transparent';
          strip.backShade.style.background =
            angle >= 90 ? `linear-gradient(to left, ${edges[i]}, ${edges[i + 1]})` : 'transparent';
        });

        if (plan.ghost) {
          // In single-page view the turned page melts into the background as it lands in the
          // empty half. The paper stays solid (see-through strips would show their overlaps)
          // and its colour runs from dark paper to the reader's own background.
          const visible = forward
            ? piecewise([[0, 1], [0.5, 1], [1, 0]], q)
            : piecewise([[0, 0], [0.5, 1], [1, 1]], q);
          const mix = (paper, background) => Math.round(background + (paper - background) * visible);
          sheet.style.setProperty('--reader-turn-wash', `rgb(${mix(34, 11)}, ${mix(34, 11)}, ${mix(42, 15)})`);
        }

        if (revealShadow) {
          // The lifted page shades the one coming into view just beyond its free edge, softly
          // and more widely the higher it rises, gone once it has crossed the spine.
          const edge = spineX + edgeX(q);
          const reveal = plan.reveal.rect;
          const fromSpine = forward ? edge - reveal.x : reveal.x + reveal.w - edge;
          const spread = reveal.w * (0.12 + 0.3 * Math.sin(Math.PI * Math.min(q, 0.5)));
          const strength = 0.5 * piecewise([[0, 0.6], [0.25, 1], [0.6, 0.6], [0.85, 0]], q);
          revealShadow.style.opacity = '1';
          revealShadow.style.background = `linear-gradient(to ${forward ? 'right' : 'left'}, rgba(0, 0, 0, ${strength.toFixed(3)}) ${Math.max(0, fromSpine - 6).toFixed(1)}px, rgba(0, 0, 0, 0) ${Math.max(0, fromSpine + spread).toFixed(1)}px)`;
        }

        if (landShadow) {
          landShadow.style.opacity = piecewise([[0, 0], [0.5, 0], [1, 0.8]], q).toFixed(3);
        }
      };
    }

    let progress = 0;
    let target = 1;
    let frame = 0;
    let settled = false;
    let resolveDone;
    const done = new Promise((resolve) => {
      resolveDone = resolve;
    });

    const settle = () => {
      if (settled) return;
      settled = true;
      cancelAnimationFrame(frame);
      progress = target;
      render(progress);
      layer.remove();
      resolveDone(target === 0 ? 'cancelled' : 'finished');
    };

    const animateTo = (to, ms, easing) => {
      target = to;
      cancelAnimationFrame(frame);

      const start = performance.now();
      const startProgress = progress;
      const step = (now) => {
        const t = ms > 0 ? clamp((now - start) / ms, 0, 1) : 1;
        progress = startProgress + (to - startProgress) * easing(t);
        render(progress);

        if (t < 1) {
          frame = requestAnimationFrame(step);
        } else {
          settle();
        }
      };

      frame = requestAnimationFrame(step);
    };

    render(0);
    container.appendChild(layer);

    const turn = {
      kind,
      pageWidth: plan ? plan.front.rect.w : 0,
      progressForEdge: (x) => (progressForEdge ? progressForEdge(x) : 0),
      done,
      finish() {
        settle();
      },
      setProgress(next) {
        if (settled) return;
        cancelAnimationFrame(frame);
        target = 1;
        progress = clamp(next, 0, 1);
        render(progress);
      },
      release(complete) {
        if (settled) return;
        const to = complete ? 1 : 0;
        const ms = Math.max(120, Math.abs(to - progress) * duration * 0.75);
        animateTo(to, ms, easeOutCubic);
      }
    };

    // Lets QA scripts hold a turn at a given point.
    layer.readerTurn = turn;

    if (!scrub) {
      // The strips carry the page's own easing, so the clock itself only softens the ends.
      animateTo(1, duration, kind === 'fade' ? easeOutCubic : easeInOutSine);
    }

    return turn;
  }

  async function restoreScrollProgress(manifest, pages) {
    const saved = getSavedProgress(manifest.handle);
    if (!saved || typeof saved.pageIndex !== 'number') return false;

    const safeIndex = clamp(saved.pageIndex, 0, pages.length - 1);
    const targetPage = pages[safeIndex];
    if (!targetPage) return false;

    const targetImg = targetPage.querySelector('img');
    await waitForImage(targetImg);

    const top =
      targetPage.offsetTop +
      targetPage.offsetHeight * clamp(saved.progress || 0, 0, 1) -
      SCROLL_OFFSET;

    window.scrollTo({
      top: Math.max(0, top),
      behavior: 'auto'
    });

    return true;
  }

  function getBackHref() {
    try {
      if (!document.referrer) return '/collections/all';

      const ref = new URL(document.referrer);
      if (ref.origin === window.location.origin) {
        return `${ref.pathname}${ref.search}${ref.hash}`;
      }
    } catch (_) {}

    return '/collections/all';
  }

  function getStatusText(pageIndex, totalPages, isDesktopSpread, desktopViewMode) {
    const currentPage = clamp(pageIndex + 1, 1, totalPages);

    if (isDesktopSpread && desktopViewMode === DESKTOP_VIEW_MODE_GRID) {
      return `Grid · ${totalPages} pages`;
    }

    if (!isDesktopSpread || desktopViewMode === DESKTOP_VIEW_MODE_SINGLE) {
      return `Page ${currentPage} of ${totalPages}`;
    }

    const spreadStart = pageIndex % 2 === 0 ? currentPage : currentPage - 1;
    const spreadEnd = Math.min(spreadStart + 1, totalPages);

    if (spreadStart === spreadEnd) {
      return `Page ${spreadStart} of ${totalPages}`;
    }

    return `Pages ${spreadStart}–${spreadEnd} of ${totalPages}`;
  }

  function getScrollProgressPercent() {
    const maxScroll = document.documentElement.scrollHeight - window.innerHeight;

    if (maxScroll <= 1) {
      return 0;
    }

    return clamp((window.scrollY / maxScroll) * 100, 0, 100);
  }

  function getDesktopProgressPercent(pageIndex, totalPages) {
    if (totalPages <= 1) {
      return 0;
    }

    return clamp((pageIndex / (totalPages - 1)) * 100, 0, 100);
  }

  function isInteractiveElement(el) {
    if (!el || !(el instanceof Element)) return false;

    if (el.isContentEditable) return true;

    return Boolean(
      el.closest('a, button, input, textarea, select, [contenteditable="true"]')
    );
  }

  // Arrow keys belong to the reader unless someone is typing. A focused button (say the
  // → button just clicked) has no use for them, so it must not swallow page turns.
  function isTypingElement(el) {
    if (!el || !(el instanceof Element)) return false;

    if (el.isContentEditable) return true;

    return Boolean(el.closest('input, textarea, select, [contenteditable="true"]'));
  }

  function getSpreadStartIndex(pageIndex) {
    return pageIndex % 2 === 0 ? pageIndex : Math.max(pageIndex - 1, 0);
  }

  function getSpreadIndexFromPageIndex(pageIndex) {
    return Math.floor(getSpreadStartIndex(pageIndex) / 2);
  }

  function scrollToPage(pages, pageIndex) {
    const targetPage = pages[pageIndex];
    if (!targetPage) return;

    window.scrollTo({
      top: Math.max(0, targetPage.offsetTop - SCROLL_OFFSET),
      behavior: 'auto'
    });
  }

  function createTopbar(manifest, totalPages, isDesktopSpread, desktopViewMode) {
    const topbar = document.createElement('div');
    topbar.className = 'reader-topbar';

    const left = document.createElement('div');
    left.className = 'reader-topbar__left';

    const back = document.createElement('a');
    back.className = 'reader-topbar__back';
    back.href = getBackHref();
    back.textContent = 'Back';

    const meta = document.createElement('div');
    meta.className = 'reader-topbar__meta';

    const title = document.createElement('h1');
    title.className = 'reader-topbar__title';
    title.textContent = manifest.title || 'Comic';

    const format = document.createElement('div');
    format.className = 'reader-topbar__format';
    format.textContent =
      manifest.format === 'compact' ? 'Compact comic' : 'Standard comic';

    const status = document.createElement('div');
    status.className = 'reader-topbar__status';
    status.textContent = getStatusText(0, totalPages, isDesktopSpread, desktopViewMode);

    const right = document.createElement('div');
    right.className = 'reader-topbar__right';

    // Phones and tablets only: read as one long scroll, or one page at a time.
    const layoutToggle = document.createElement('div');
    layoutToggle.className = 'reader-topbar__view-toggle reader-topbar__layout-toggle';
    layoutToggle.setAttribute('role', 'group');
    layoutToggle.setAttribute('aria-label', 'Reading layout');

    const scrollLayoutButton = document.createElement('button');
    scrollLayoutButton.type = 'button';
    scrollLayoutButton.className = 'reader-topbar__view-button';
    scrollLayoutButton.textContent = 'Scroll';

    const pagesLayoutButton = document.createElement('button');
    pagesLayoutButton.type = 'button';
    pagesLayoutButton.className = 'reader-topbar__view-button';
    pagesLayoutButton.textContent = 'Pages';

    layoutToggle.appendChild(scrollLayoutButton);
    layoutToggle.appendChild(pagesLayoutButton);

    const controls = document.createElement('div');
    controls.className = 'reader-topbar__controls';

    const viewToggle = document.createElement('div');
    viewToggle.className = 'reader-topbar__view-toggle';
    viewToggle.setAttribute('role', 'group');
    viewToggle.setAttribute('aria-label', 'Desktop page layout');

    const singleButton = document.createElement('button');
    singleButton.type = 'button';
    singleButton.className = 'reader-topbar__view-button';
    singleButton.setAttribute('aria-label', 'Single page mode');
    singleButton.textContent = '1P';

    const spreadButton = document.createElement('button');
    spreadButton.type = 'button';
    spreadButton.className = 'reader-topbar__view-button';
    spreadButton.setAttribute('aria-label', 'Two page spread mode');
    spreadButton.textContent = '2P';

    const gridButton = document.createElement('button');
    gridButton.type = 'button';
    gridButton.className = 'reader-topbar__view-button';
    gridButton.setAttribute('aria-label', 'Grid mode');
    gridButton.textContent = 'Grid';

    viewToggle.appendChild(singleButton);
    viewToggle.appendChild(spreadButton);
    viewToggle.appendChild(gridButton);

    const zoomOutButton = document.createElement('button');
    zoomOutButton.type = 'button';
    zoomOutButton.className = 'reader-topbar__zoom';
    zoomOutButton.setAttribute('aria-label', 'Zoom out');
    zoomOutButton.textContent = '−';

    const zoomLabel = document.createElement('div');
    zoomLabel.className = 'reader-topbar__zoom-label';
    zoomLabel.textContent = '100%';

    const zoomInButton = document.createElement('button');
    zoomInButton.type = 'button';
    zoomInButton.className = 'reader-topbar__zoom';
    zoomInButton.setAttribute('aria-label', 'Zoom in');
    zoomInButton.textContent = '+';

    const prevButton = document.createElement('button');
    prevButton.type = 'button';
    prevButton.className = 'reader-topbar__nav';
    prevButton.setAttribute('aria-label', 'Previous page');
    prevButton.textContent = '←';

    const nextButton = document.createElement('button');
    nextButton.type = 'button';
    nextButton.className = 'reader-topbar__nav';
    nextButton.setAttribute('aria-label', 'Next page');
    nextButton.textContent = '→';

    controls.appendChild(viewToggle);
    controls.appendChild(zoomOutButton);
    controls.appendChild(zoomLabel);
    controls.appendChild(zoomInButton);
    controls.appendChild(prevButton);
    controls.appendChild(nextButton);

    const progress = document.createElement('div');
    progress.className = 'reader-progress';
    progress.setAttribute('aria-hidden', 'true');

    const progressFill = document.createElement('span');
    progressFill.className = 'reader-progress__fill';
    progress.appendChild(progressFill);

    meta.appendChild(title);
    meta.appendChild(format);

    left.appendChild(back);
    left.appendChild(meta);

    right.appendChild(status);
    right.appendChild(layoutToggle);
    right.appendChild(controls);

    topbar.appendChild(left);
    topbar.appendChild(right);
    topbar.appendChild(progress);

    return {
      topbar,
      status,
      prevButton,
      nextButton,
      progressFill,
      zoomOutButton,
      zoomInButton,
      zoomLabel,
      singleButton,
      spreadButton,
      gridButton,
      scrollLayoutButton,
      pagesLayoutButton
    };
  }

  function createResumePrompt() {
    const prompt = document.createElement('div');
    prompt.className = 'reader-resume';
    prompt.hidden = true;

    prompt.innerHTML = `
      <div class="reader-resume__text">
        Resume from page <span data-reader-resume-page>1</span>
      </div>
      <div class="reader-resume__actions">
        <button type="button" class="reader-resume__button reader-resume__button--primary" data-reader-resume-continue>
          Resume
        </button>
        <button type="button" class="reader-resume__button" data-reader-resume-reset>
          Start over
        </button>
      </div>
    `;

    return {
      prompt,
      page: prompt.querySelector('[data-reader-resume-page]'),
      continueButton: prompt.querySelector('[data-reader-resume-continue]'),
      resetButton: prompt.querySelector('[data-reader-resume-reset]')
    };
  }

  // End-of-issue email capture. Shown once per browser: after it has been seen,
  // dismissed or submitted it never comes back, so re-reading stays uninterrupted.
  function initEndCard(card, handle) {
    const key = `bhm-reader-end-card:${handle}`;
    const readState = () => {
      try {
        return localStorage.getItem(key);
      } catch (_) {
        return null;
      }
    };
    const writeState = (value) => {
      try {
        localStorage.setItem(key, value);
      } catch (_) {}
    };

    const form = card.querySelector('[data-end-card-form]');
    const pitch = card.querySelector('[data-end-card-pitch]');
    const success = card.querySelector('[data-end-card-success]');
    const error = card.querySelector('[data-end-card-error]');
    const submit = form ? form.querySelector('[type="submit"]') : null;

    const showSuccess = () => {
      writeState('subscribed');
      pitch.hidden = true;
      success.hidden = false;
      card.hidden = false;
      const skip = card.querySelector('.reader-end-card__actions [data-end-card-dismiss]');
      if (skip) skip.textContent = 'Close';
    };

    // Back from a native (non-AJAX) post: Shopify redirects with customer_posted=true
    // on success, or re-renders the form with errors.
    const params = new URLSearchParams(window.location.search);
    const returnedFromPost = params.get('customer_posted') === 'true';
    const returnedWithError = error && !error.hidden;

    if (returnedFromPost) {
      params.delete('customer_posted');
      const query = params.toString();
      history.replaceState(null, '', window.location.pathname + (query ? `?${query}` : ''));
      showSuccess();
    } else if (returnedWithError) {
      card.hidden = false;
    } else if (readState()) {
      card.remove();
      return null;
    }

    card.querySelectorAll('[data-end-card-dismiss]').forEach((button) => {
      button.addEventListener('click', () => {
        if (readState() !== 'subscribed') writeState('dismissed');
        card.hidden = true;
      });
    });

    // The form posts natively on purpose: the store's bot protection only accepts
    // customer-form posts carrying the hCaptcha token that Shopify's own script adds
    // on a real submit, so an AJAX post is rejected with "Missing CAPTCHA token".
    if (form && submit) {
      form.addEventListener('submit', () => {
        error.hidden = true;
        submit.textContent = 'Sending…';
      });
    }

    return {
      // The reader scrolls here once it has finished its own restore.
      returnedFromPost: returnedFromPost || returnedWithError,
      reveal() {
        if (!card.isConnected || !card.hidden || readState()) return;
        card.hidden = false;
        writeState('seen');
      },
      isShown() {
        return card.isConnected && !card.hidden;
      },
      scrollTo() {
        card.scrollIntoView({ behavior: 'smooth', block: 'center' });
      },
      // Pages above the card are still loading and growing after the post redirect, so
      // keep re-centring until the layout settles or the reader takes over.
      settleInView() {
        const jump = () => card.scrollIntoView({ behavior: 'instant', block: 'center' });
        const events = ['wheel', 'touchstart', 'keydown', 'pointerdown'];
        const observer = new ResizeObserver(jump);
        const stop = () => {
          observer.disconnect();
          events.forEach((name) => window.removeEventListener(name, stop));
        };

        jump();
        observer.observe(document.body);
        events.forEach((name) => window.addEventListener(name, stop, { passive: true }));
        setTimeout(stop, 6000);
      }
    };
  }

  function initReader(app) {
    const manifestId = app.getAttribute('data-manifest-id');
    const manifest = getManifest(manifestId);

    if (!manifest || !Array.isArray(manifest.pages) || manifest.pages.length === 0) {
      app.innerHTML =
        '<div class="reader-empty"><h1>No pages found</h1><p>This reader has no pages yet.</p></div>';
      return;
    }

    const pageUrls = manifest.pages.filter(Boolean);
    const readerTitle = manifest.title || app.dataset.readerTitle || 'Comic';

    // The reader restores its own place (and offers Resume), so the browser's own scroll
    // restore on reload would only fight it and push the prompt under the top bar.
    if ('scrollRestoration' in history) {
      history.scrollRestoration = 'manual';
    }

    app.innerHTML = '';

    const endCardEl = app.dataset.endCardId ? document.getElementById(app.dataset.endCardId) : null;
    const endCard = endCardEl ? initEndCard(endCardEl, manifest.handle) : null;

    const desktopSpreadQuery = window.matchMedia(DESKTOP_SPREAD_MEDIA);
    let phonePages = getPhoneLayout() === PHONE_LAYOUT_PAGES;
    // Desktop always shows pages; below the desktop width it is the reader's choice.
    const isPaged = () => desktopSpreadQuery.matches || phonePages;
    let desktopViewMode = DESKTOP_VIEW_MODE_SPREAD;
    // Pages mode on a phone is always one page; the desktop layout comes back on a wide screen.
    let desktopModeBeforePhone = null;

    if (!desktopSpreadQuery.matches && phonePages) {
      desktopModeBeforePhone = desktopViewMode;
      desktopViewMode = DESKTOP_VIEW_MODE_SINGLE;
    }

    const {
      topbar,
      status,
      prevButton,
      nextButton,
      progressFill,
      zoomOutButton,
      zoomInButton,
      zoomLabel,
      singleButton,
      spreadButton,
      gridButton,
      scrollLayoutButton,
      pagesLayoutButton
    } = createTopbar(manifest, pageUrls.length, isPaged(), desktopViewMode);

    const resume = createResumePrompt();

    const stage = document.createElement('div');
    stage.className = 'reader-stage';

    const stageCanvas = document.createElement('div');
    stageCanvas.className = 'reader-stage__canvas';

    const stageGrid = document.createElement('div');
    stageGrid.className = 'reader-grid';
    stageGrid.hidden = true;

    stage.appendChild(stageCanvas);
    stage.appendChild(stageGrid);

    app.appendChild(topbar);
    app.appendChild(resume.prompt);
    app.appendChild(stage);

    const saved = getSavedProgress(manifest.handle);
    const shouldOfferResume =
      saved &&
      typeof saved.pageIndex === 'number' &&
      (saved.pageIndex > 0 || clamp(saved.progress || 0, 0, 1) > 0.02);

    if (shouldOfferResume) {
      const safeResumePage = clamp(saved.pageIndex + 1, 1, pageUrls.length);
      resume.page.textContent = String(safeResumePage);
      resume.prompt.hidden = false;
    } else {
      resume.prompt.hidden = true;
    }

    let ticking = false;
    let isRestoring = true;
    let isResumeChoicePending = Boolean(shouldOfferResume);
    let isAnimating = false;
    let activeSpreadIndex = 0;
    let activeDesktopPageIndex = 0;
    let wasPaged = isPaged();
    let desktopZoom = 1;
    let activeTurn = null;
    let wheelDelta = 0;
    let wheelGestureUsed = false;
    let wheelGestureTimer = null;
    let drag = null;
    let suppressClickUntil = 0;

    const isAtDesktopEnd = () =>
      desktopViewMode === DESKTOP_VIEW_MODE_SPREAD
        ? activeSpreadIndex >= spreads.length - 1
        : activeDesktopPageIndex >= pageUrls.length - 1;

    const updateEndCard = (pageIndex) => {
      if (!endCard) return;

      if (isPaged()) {
        if (desktopViewMode === DESKTOP_VIEW_MODE_GRID) return;
        if (isAtDesktopEnd()) {
          endCard.reveal();
          setDesktopNavState();
        }
        return;
      }

      if (pageIndex >= pageUrls.length - 1) endCard.reveal();
    };

    const dismissResumePrompt = () => {
      isResumeChoicePending = false;
      resume.prompt.hidden = true;
      fitPagedStage();
    };

    // On a phone in Pages mode the page fills what is left of the screen under the top bar.
    const fitPagedStage = () => {
      if (desktopSpreadQuery.matches || !phonePages) {
        stage.style.removeProperty('--reader-paged-height');
        return;
      }

      const topbarSpace = topbar.offsetHeight + 8 + 12;
      const resumeSpace = resume.prompt.hidden ? 0 : resume.prompt.offsetHeight + 14;
      const height = Math.max(320, window.innerHeight - topbarSpace - resumeSpace - 16);
      stage.style.setProperty('--reader-paged-height', `${Math.round(height)}px`);
    };

    const scrollPagedStageIntoView = () => {
      window.scrollTo({
        top: Math.max(0, app.offsetTop - (desktopSpreadQuery.matches ? DESKTOP_STAGE_OFFSET : 8)),
        behavior: 'auto'
      });
    };

    const updateLayoutToggleUI = () => {
      scrollLayoutButton.setAttribute('aria-pressed', phonePages ? 'false' : 'true');
      pagesLayoutButton.setAttribute('aria-pressed', phonePages ? 'true' : 'false');
    };

    const pages = pageUrls.map((url, index) => {
      const page = createPage(url, index, readerTitle);

      page.addEventListener('click', () => {
        if (isResumeChoicePending) {
          dismissResumePrompt();
        }
      });

      return page;
    });

    const spreads = [];

    for (let i = 0; i < pages.length; i += 2) {
      const spreadPages = pages.slice(i, i + 2);
      const spread = createSpread(spreads.length, spreadPages);
      stageCanvas.appendChild(spread);
      spreads.push(spread);
    }

    const gridItems = pageUrls.map((url, index) => {
      const item = createGridItem(url, index, readerTitle);
      stageGrid.appendChild(item);
      return item;
    });

    const applyDesktopZoom = () => {
      if (!desktopSpreadQuery.matches || desktopViewMode === DESKTOP_VIEW_MODE_GRID) {
        stage.style.setProperty('--reader-zoom', '1');
        return;
      }

      stage.style.setProperty('--reader-zoom', String(desktopZoom));
    };

    const updateZoomUI = () => {
      const isDesktopReading =
        desktopSpreadQuery.matches && desktopViewMode !== DESKTOP_VIEW_MODE_GRID;
      const zoomPercent = Math.round(desktopZoom * 100);

      zoomLabel.textContent = `${zoomPercent}%`;
      zoomOutButton.disabled = !isDesktopReading || desktopZoom <= DESKTOP_ZOOM_MIN;
      zoomInButton.disabled = !isDesktopReading || desktopZoom >= DESKTOP_ZOOM_MAX;
    };

    const updateGridUI = () => {
      const isGridMode =
        desktopSpreadQuery.matches && desktopViewMode === DESKTOP_VIEW_MODE_GRID;

      stageGrid.hidden = !isGridMode;

      gridItems.forEach((item, index) => {
        const isActive = index === activeDesktopPageIndex;
        item.classList.toggle('reader-grid__item--active', isActive);
        item.setAttribute('aria-current', isActive ? 'page' : 'false');
      });
    };

    const updateViewModeUI = () => {
      const isDesktop = desktopSpreadQuery.matches;

      singleButton.disabled = !isDesktop || desktopViewMode === DESKTOP_VIEW_MODE_SINGLE;
      spreadButton.disabled = !isDesktop || desktopViewMode === DESKTOP_VIEW_MODE_SPREAD;
      gridButton.disabled = !isDesktop || desktopViewMode === DESKTOP_VIEW_MODE_GRID;

      singleButton.setAttribute(
        'aria-pressed',
        desktopViewMode === DESKTOP_VIEW_MODE_SINGLE ? 'true' : 'false'
      );
      spreadButton.setAttribute(
        'aria-pressed',
        desktopViewMode === DESKTOP_VIEW_MODE_SPREAD ? 'true' : 'false'
      );
      gridButton.setAttribute(
        'aria-pressed',
        desktopViewMode === DESKTOP_VIEW_MODE_GRID ? 'true' : 'false'
      );

      stage.dataset.desktopMode = desktopViewMode;
      updateGridUI();
    };

    const setDesktopZoom = (nextZoom) => {
      if (!desktopSpreadQuery.matches) return;
      if (desktopViewMode === DESKTOP_VIEW_MODE_GRID) return;

      const previousZoom = desktopZoom;
      const viewportWidth = stage.clientWidth;
      const viewportHeight = stage.clientHeight;
      const centerX = stage.scrollLeft + viewportWidth / 2;
      const centerY = stage.scrollTop + viewportHeight / 2;

      desktopZoom = clamp(
        Math.round(nextZoom * 100) / 100,
        DESKTOP_ZOOM_MIN,
        DESKTOP_ZOOM_MAX
      );

      applyDesktopZoom();
      updateZoomUI();

      const ratio = desktopZoom / previousZoom;

      requestAnimationFrame(() => {
        stage.scrollLeft = Math.max(0, centerX * ratio - viewportWidth / 2);
        stage.scrollTop = Math.max(0, centerY * ratio - viewportHeight / 2);
      });
    };

    const resetDesktopZoom = () => {
      desktopZoom = 1;
      applyDesktopZoom();
      updateZoomUI();

      if (desktopSpreadQuery.matches) {
        stage.scrollLeft = 0;
        stage.scrollTop = 0;
      }
    };

    const setSpreadState = (activeIndex) => {
      const isGridMode =
        desktopSpreadQuery.matches && desktopViewMode === DESKTOP_VIEW_MODE_GRID;

      spreads.forEach((spread, index) => {
        const isActive = !isGridMode && index === activeIndex;
        spread.classList.toggle('reader-spread--active', isActive);
        spread.hidden = !isActive;
        spread.classList.remove('reader-spread--desktop-single');

        const spreadPages = Array.from(spread.querySelectorAll('.reader-page'));
        spreadPages.forEach((page) => {
          page.classList.remove('reader-page--desktop-hidden');
        });

        if (isActive && isPaged() && desktopViewMode === DESKTOP_VIEW_MODE_SINGLE) {
          const spreadStartIndex = clamp(
            Number(spread.dataset.startPageIndex || 0),
            0,
            pageUrls.length - 1
          );
          const visibleOffset = clamp(
            activeDesktopPageIndex - spreadStartIndex,
            0,
            Math.max(spreadPages.length - 1, 0)
          );

          spread.classList.add('reader-spread--desktop-single');

          spreadPages.forEach((page, pageOffset) => {
            if (pageOffset !== visibleOffset) {
              page.classList.add('reader-page--desktop-hidden');
            }
          });
        }

        spread.style.opacity = '';
        spread.style.transform = '';
        spread.style.zIndex = '';
      });

      applyDesktopZoom();
      updateViewModeUI();
    };

    const getActiveDesktopPageIndex = () => {
      return clamp(activeDesktopPageIndex, 0, pageUrls.length - 1);
    };

    const getCurrentPosition = () => {
      if (isPaged()) {
        return {
          pageIndex: getActiveDesktopPageIndex(),
          progress: 0
        };
      }

      return getCurrentScrollPosition(pages);
    };

    const setDesktopNavState = () => {
      let canPrev = false;
      let canNext = false;

      if (isPaged() && desktopViewMode !== DESKTOP_VIEW_MODE_GRID) {
        if (desktopViewMode === DESKTOP_VIEW_MODE_SINGLE) {
          canPrev = activeDesktopPageIndex > 0;
          canNext = activeDesktopPageIndex < pageUrls.length - 1;
        } else {
          canPrev = activeSpreadIndex > 0;
          canNext = activeSpreadIndex < spreads.length - 1;
        }

        // Past the last page, "next" leads to the end-of-issue card below the stage.
        if (!canNext && endCard && endCard.isShown()) canNext = true;
      }

      prevButton.disabled = !canPrev;
      nextButton.disabled = !canNext;
    };

    const update = ({ save = true } = {}) => {
      const { pageIndex, progress } = getCurrentPosition();
      const paged = isPaged();

      status.textContent = getStatusText(pageIndex, pageUrls.length, paged, desktopViewMode);

      progressFill.style.width = `${
        paged
          ? getDesktopProgressPercent(pageIndex, pageUrls.length)
          : getScrollProgressPercent()
      }%`;

      setDesktopNavState();
      updateZoomUI();
      updateViewModeUI();

      if (save && !isRestoring && !isResumeChoicePending) {
        saveProgress(manifest.handle, pageIndex, progress);
      }

      updateEndCard(pageIndex);
    };

    // A new page request skips the turn in progress to its end, so fast readers never wait.
    const finishActiveTurn = async () => {
      const turn = activeTurn;
      if (!turn) return;

      turn.finish();
      await turn.done;
    };

    const canTurnPages = () =>
      isPaged() &&
      desktopViewMode !== DESKTOP_VIEW_MODE_GRID &&
      desktopZoom === 1 &&
      !prefersReducedMotion();

    const getVisiblePageFaces = () => {
      const spread = spreads[activeSpreadIndex];
      if (!spread || spread.hidden) return [];

      const origin = stageCanvas.getBoundingClientRect();
      const visible = Array.from(spread.querySelectorAll('.reader-page')).filter(
        (page) => !page.classList.contains('reader-page--desktop-hidden')
      );

      return visible.map((page, index) => {
        const img = page.querySelector('img');
        const side = visible.length === 2 ? (index === 0 ? 'left' : 'right') : 'center';

        return { src: img.currentSrc || img.src, side, rect: getContentRect(img, side, origin) };
      });
    };

    // `turn` (+1 / -1) animates the change. With `onDragTurn` the turn is handed over paused
    // for a finger to drive, and letting go short of the page puts the old page back.
    const showDesktopSpread = async (
      nextSpreadIndex,
      { save = true, targetPageIndex = null, turn = 0, onDragTurn = null } = {}
    ) => {
      await finishActiveTurn();
      if (!spreads.length || isAnimating) return false;

      const safeIndex = clamp(nextSpreadIndex, 0, spreads.length - 1);
      const nextSpread = spreads[safeIndex];
      if (!nextSpread) return false;

      isAnimating = true;
      setDesktopNavState();

      if (desktopViewMode !== DESKTOP_VIEW_MODE_GRID) {
        primeSpreadImages(nextSpread);
      }

      let outgoingFaces = null;

      if (turn !== 0 && canTurnPages()) {
        outgoingFaces = getVisiblePageFaces();
        await waitForSpreadImages(nextSpread, TURN_LOAD_TIMEOUT);
      }

      const spreadPages = Array.from(nextSpread.querySelectorAll('.reader-page'));
      const spreadStartIndex = clamp(
        Number(nextSpread.dataset.startPageIndex || 0),
        0,
        pageUrls.length - 1
      );
      const spreadEndIndex = clamp(
        spreadStartIndex + spreadPages.length - 1,
        spreadStartIndex,
        pageUrls.length - 1
      );

      const previousSpreadIndex = activeSpreadIndex;
      const previousPageIndex = activeDesktopPageIndex;

      activeSpreadIndex = safeIndex;

      if (typeof targetPageIndex === 'number') {
        activeDesktopPageIndex = clamp(targetPageIndex, spreadStartIndex, spreadEndIndex);
      } else if (desktopViewMode === DESKTOP_VIEW_MODE_SINGLE) {
        activeDesktopPageIndex = clamp(
          activeDesktopPageIndex,
          spreadStartIndex,
          spreadEndIndex
        );
      } else if (desktopViewMode === DESKTOP_VIEW_MODE_SPREAD) {
        activeDesktopPageIndex = spreadStartIndex;
      }

      setSpreadState(safeIndex);

      if (desktopViewMode !== DESKTOP_VIEW_MODE_GRID) {
        primeSpreadImages(spreads[safeIndex + 1]);
        primeSpreadImages(spreads[safeIndex - 1]);
      }

      update({ save });

      if (outgoingFaces) {
        const playing = playPageTurn(stageCanvas, turn, outgoingFaces, getVisiblePageFaces(), {
          scrub: Boolean(onDragTurn)
        });

        if (playing) {
          activeTurn = playing;
          stage.classList.add('reader-stage--turning');
          if (onDragTurn) onDragTurn(playing);

          const outcome = await playing.done;
          stage.classList.remove('reader-stage--turning');
          activeTurn = null;

          if (outcome === 'cancelled') {
            activeSpreadIndex = previousSpreadIndex;
            activeDesktopPageIndex = previousPageIndex;
            setSpreadState(previousSpreadIndex);
            update({ save });
          }
        }
      }

      isAnimating = false;
      return true;
    };

    // Moves between the paged layout (desktop, or Pages on a phone) and the long scroll,
    // keeping the reader on the page they were looking at.
    const syncDesktopMode = async ({ preservePosition = true } = {}) => {
      await finishActiveTurn();
      const isDesktop = desktopSpreadQuery.matches;
      const paged = isPaged();
      const referencePageIndex = wasPaged
        ? getActiveDesktopPageIndex()
        : preservePosition
          ? getCurrentScrollPosition(pages).pageIndex
          : 0;

      if (paged && !isDesktop && desktopViewMode !== DESKTOP_VIEW_MODE_SINGLE) {
        desktopModeBeforePhone = desktopViewMode;
        desktopViewMode = DESKTOP_VIEW_MODE_SINGLE;
      } else if (isDesktop && desktopModeBeforePhone) {
        desktopViewMode = desktopModeBeforePhone;
        desktopModeBeforePhone = null;
      }

      stage.classList.toggle(
        'reader-stage--paged',
        paged && desktopViewMode !== DESKTOP_VIEW_MODE_GRID
      );
      stage.classList.toggle(
        'reader-stage--desktop-grid',
        isDesktop && desktopViewMode === DESKTOP_VIEW_MODE_GRID
      );
      fitPagedStage();

      if (paged) {
        applyDesktopZoom();
        if (!wasPaged) stageGrid.hidden = true;

        await showDesktopSpread(getSpreadIndexFromPageIndex(referencePageIndex), {
          save: false,
          targetPageIndex: referencePageIndex
        });

        scrollPagedStageIntoView();

        stage.scrollLeft = 0;
        stage.scrollTop = 0;
      } else {
        resetDesktopZoom();

        spreads.forEach((spread) => {
          spread.hidden = false;
          spread.classList.remove('reader-spread--active');
          spread.classList.remove('reader-spread--desktop-single');
          spread.style.opacity = '';
          spread.style.transform = '';
          spread.style.zIndex = '';

          Array.from(spread.querySelectorAll('.reader-page')).forEach((page) => {
            page.classList.remove('reader-page--desktop-hidden');
          });
        });

        stageGrid.hidden = true;
        stage.style.setProperty('--reader-zoom', '1');

        requestAnimationFrame(() => {
          scrollToPage(pages, clamp(referencePageIndex, 0, pages.length - 1));
          update({ save: false });
        });
      }

      wasPaged = paged;
      setDesktopNavState();
      updateZoomUI();
      updateViewModeUI();
    };

    const setDesktopViewMode = async (nextMode) => {
      if (!desktopSpreadQuery.matches) return;
      if (
        nextMode !== DESKTOP_VIEW_MODE_SINGLE &&
        nextMode !== DESKTOP_VIEW_MODE_SPREAD &&
        nextMode !== DESKTOP_VIEW_MODE_GRID
      ) {
        return;
      }
      if (desktopViewMode === nextMode) return;

      const referencePageIndex = getActiveDesktopPageIndex();

      isRestoring = true;
      desktopViewMode = nextMode;

      stage.classList.toggle(
        'reader-stage--paged',
        desktopViewMode !== DESKTOP_VIEW_MODE_GRID
      );
      stage.classList.toggle(
        'reader-stage--desktop-grid',
        desktopViewMode === DESKTOP_VIEW_MODE_GRID
      );

      if (desktopViewMode === DESKTOP_VIEW_MODE_GRID) {
        resetDesktopZoom();
      }

      await showDesktopSpread(getSpreadIndexFromPageIndex(referencePageIndex), {
        save: false,
        targetPageIndex: referencePageIndex
      });

      requestAnimationFrame(() => {
        update({ save: false });
        isRestoring = false;
      });
    };

    const openGridPage = async (pageIndex) => {
      if (!desktopSpreadQuery.matches) return;

      if (isResumeChoicePending) {
        dismissResumePrompt();
      }

      isRestoring = true;
      activeDesktopPageIndex = clamp(pageIndex, 0, pageUrls.length - 1);
      desktopViewMode = DESKTOP_VIEW_MODE_SINGLE;

      stage.classList.add('reader-stage--paged');
      stage.classList.remove('reader-stage--desktop-grid');

      await showDesktopSpread(getSpreadIndexFromPageIndex(activeDesktopPageIndex), {
        save: false,
        targetPageIndex: activeDesktopPageIndex
      });

      // The grid is taller than the paged stage, so bring the opened page back into view.
      window.scrollTo({
        top: Math.max(0, app.offsetTop - DESKTOP_STAGE_OFFSET),
        behavior: 'auto'
      });

      requestAnimationFrame(() => {
        update({ save: true });
        isRestoring = false;
      });
    };

    const performDesktopJump = async (direction, { onDragTurn = null } = {}) => {
      if (!isPaged()) return false;
      if (desktopViewMode === DESKTOP_VIEW_MODE_GRID) return false;

      if (isResumeChoicePending) {
        dismissResumePrompt();
      }

      if (direction > 0 && endCard && endCard.isShown() && isAtDesktopEnd()) {
        endCard.scrollTo();
        return true;
      }

      if (desktopViewMode === DESKTOP_VIEW_MODE_SINGLE) {
        const nextPageIndex = clamp(
          activeDesktopPageIndex + direction,
          0,
          pageUrls.length - 1
        );

        if (nextPageIndex === activeDesktopPageIndex) return false;

        await showDesktopSpread(getSpreadIndexFromPageIndex(nextPageIndex), {
          save: true,
          targetPageIndex: nextPageIndex,
          turn: direction,
          onDragTurn
        });

        return true;
      }

      const nextSpreadIndex = clamp(activeSpreadIndex + direction, 0, spreads.length - 1);
      if (nextSpreadIndex === activeSpreadIndex) return false;

      const nextSpread = spreads[nextSpreadIndex];
      const nextPageIndex = clamp(
        Number(nextSpread?.dataset.startPageIndex || 0),
        0,
        pageUrls.length - 1
      );

      await showDesktopSpread(nextSpreadIndex, {
        save: true,
        targetPageIndex: nextPageIndex,
        turn: direction,
        onDragTurn
      });

      return true;
    };

    if (isPaged()) {
      stage.classList.add('reader-stage--paged');
      stage.style.setProperty('--reader-zoom', '1');
      setSpreadState(0);
      primeSpreadImages(spreads[0]);
      primeSpreadImages(spreads[1]);
    }

    const onScroll = () => {
      if (isPaged()) return;
      if (ticking || isRestoring) return;

      if (isResumeChoicePending && window.scrollY > 24) {
        dismissResumePrompt();
      }

      ticking = true;
      requestAnimationFrame(() => {
        update();
        ticking = false;
      });
    };

    const onKeydown = (event) => {
      if (!isPaged()) return;
      if (desktopViewMode === DESKTOP_VIEW_MODE_GRID) return;
      if (event.defaultPrevented) return;
      if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
      if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
      if (isTypingElement(event.target) || isTypingElement(document.activeElement)) return;

      event.preventDefault();
      const direction = event.key === 'ArrowRight' ? 1 : -1;
      performDesktopJump(direction);
    };

    // Desktop: either half of the book. Phone: the outer edges only, so a tap in the
    // middle of a page (or the tail of a swipe) never turns it.
    const onStageClick = (event) => {
      if (!isPaged()) return;
      if (desktopViewMode === DESKTOP_VIEW_MODE_GRID) return;
      if (event.defaultPrevented) return;
      if (isInteractiveElement(event.target)) return;
      if (performance.now() < suppressClickUntil) return;

      const rect = stage.getBoundingClientRect();
      if (!rect.width) return;

      const clickX = (event.clientX - rect.left) / rect.width;
      const edge = desktopSpreadQuery.matches ? 0.5 : TAP_EDGE_FRACTION;

      if (clickX < edge) performDesktopJump(-1);
      else if (clickX > 1 - edge) performDesktopJump(1);
    };

    // Touch swipes on the paged stage: the page follows the finger, then finishes or falls
    // back on release. Vertical moves stay with the browser (scroll), and nothing turns
    // while two fingers are down or the page is pinch-zoomed.
    const isPinchZoomed = () =>
      Boolean(window.visualViewport) && window.visualViewport.scale > 1.01;

    const syncPinchState = () => {
      stage.classList.toggle('reader-stage--pinched', isPinchZoomed());
    };

    const getDragProgress = (turn, dx, direction) => {
      if (turn.kind === 'fade') return clamp(Math.abs(dx) / 160, 0, 1);

      // The free edge follows the finger: it starts at the page's outer edge (forward), the
      // left page's outer edge (back, two-page), or the spine (back, one page, where the
      // previous page has nowhere visible to lie), and moves as far as the finger has.
      const width = turn.pageWidth;
      const start = direction > 0 ? width : turn.kind === 'single' ? 0 : -width;
      return turn.progressForEdge(start + dx);
    };

    const settleDrag = (current) => {
      const turn = current.turn;
      if (!turn || turn !== activeTurn) return;

      // Judged on how far the finger travelled, not on the turn's progress: the edge lifts a
      // long way before the rest of the page moves, so progress races ahead of the finger.
      const travelled = (current.direction > 0 ? -current.dx : current.dx) / Math.max(turn.pageWidth, 160);
      const towardsTurn = current.direction > 0 ? -current.velocity : current.velocity;
      const complete =
        travelled > DRAG_COMMIT_DISTANCE ||
        (towardsTurn > DRAG_FLICK_SPEED && travelled > DRAG_FLICK_DISTANCE);

      turn.release(complete);
    };

    const endDrag = (event) => {
      const current = drag;
      if (!current || (event && event.pointerId !== current.id)) return;

      drag = null;
      if (current.state === 'pending') return;

      current.ended = true;
      suppressClickUntil = performance.now() + 400;
      settleDrag(current);
    };

    const onStagePointerDown = (event) => {
      if (event.pointerType === 'mouse') return;

      if (!event.isPrimary) {
        // A second finger means a pinch: drop any turn the first finger started.
        if (drag && drag.turn) drag.turn.release(false);
        drag = null;
        return;
      }

      if (!isPaged() || desktopViewMode === DESKTOP_VIEW_MODE_GRID || desktopZoom !== 1) return;
      if (isPinchZoomed() || isInteractiveElement(event.target)) return;

      drag = {
        id: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        lastX: event.clientX,
        lastTime: event.timeStamp,
        dx: 0,
        velocity: 0,
        direction: 0,
        state: 'pending',
        turn: null,
        ended: false
      };
    };

    const onStagePointerMove = (event) => {
      const current = drag;
      if (!current || event.pointerId !== current.id) return;

      const dx = event.clientX - current.startX;
      const dy = event.clientY - current.startY;

      if (current.state === 'pending') {
        if (Math.abs(dx) < DRAG_START_DISTANCE && Math.abs(dy) < DRAG_START_DISTANCE) return;

        if (Math.abs(dx) < Math.abs(dy) * 1.2) {
          drag = null;
          return;
        }

        current.state = 'dragging';
        current.direction = dx < 0 ? 1 : -1;

        if (isResumeChoicePending) dismissResumePrompt();

        performDesktopJump(current.direction, {
          onDragTurn: (turn) => {
            current.turn = turn;
            turn.setProgress(getDragProgress(turn, current.dx, current.direction));
            // The finger may already have lifted while the next page was loading.
            if (current.ended) settleDrag(current);
          }
        });
      }

      const elapsed = event.timeStamp - current.lastTime;
      if (elapsed > 0) {
        const speed = (event.clientX - current.lastX) / elapsed;
        current.velocity = current.velocity * 0.4 + speed * 0.6;
      }

      current.lastX = event.clientX;
      current.lastTime = event.timeStamp;
      current.dx = dx;

      if (current.turn && current.turn === activeTurn) {
        current.turn.setProgress(getDragProgress(current.turn, dx, current.direction));
      }
    };

    // A sideways trackpad swipe turns one page per gesture. Momentum keeps sending
    // wheel events after the fingers lift, so a gesture only ends after a short quiet gap.
    const onStageWheel = (event) => {
      if (!desktopSpreadQuery.matches) return;
      if (desktopViewMode === DESKTOP_VIEW_MODE_GRID || desktopZoom !== 1) return;
      if (Math.abs(event.deltaX) <= Math.abs(event.deltaY)) return;

      // Also stops the browser's swipe-to-go-back.
      event.preventDefault();

      clearTimeout(wheelGestureTimer);
      wheelGestureTimer = setTimeout(() => {
        wheelDelta = 0;
        wheelGestureUsed = false;
      }, WHEEL_GESTURE_GAP);

      if (wheelGestureUsed) return;

      wheelDelta += event.deltaX;
      if (Math.abs(wheelDelta) < WHEEL_TURN_THRESHOLD) return;

      wheelGestureUsed = true;
      performDesktopJump(wheelDelta > 0 ? 1 : -1);
    };

    gridItems.forEach((item) => {
      item.addEventListener('click', () => {
        const pageIndex = Number(item.dataset.pageIndex || 0);
        openGridPage(pageIndex);
      });
    });

    resume.continueButton.addEventListener('click', async () => {
      dismissResumePrompt();
      isRestoring = true;

      if (isPaged()) {
        const savedProgress = getSavedProgress(manifest.handle);
        const pageIndex = clamp(savedProgress?.pageIndex || 0, 0, pages.length - 1);

        await showDesktopSpread(getSpreadIndexFromPageIndex(pageIndex), {
          save: false,
          targetPageIndex: pageIndex
        });

        scrollPagedStageIntoView();

        stage.scrollLeft = 0;
        stage.scrollTop = 0;
      } else {
        await restoreScrollProgress(manifest, pages);
      }

      requestAnimationFrame(() => {
        update({ save: false });
        isRestoring = false;
      });
    });

    resume.resetButton.addEventListener('click', async () => {
      dismissResumePrompt();
      clearSavedProgress(manifest.handle);
      isRestoring = true;

      if (isPaged()) {
        resetDesktopZoom();

        activeDesktopPageIndex = 0;
        desktopViewMode = desktopSpreadQuery.matches
          ? DESKTOP_VIEW_MODE_SPREAD
          : DESKTOP_VIEW_MODE_SINGLE;

        stage.classList.add('reader-stage--paged');
        stage.classList.remove('reader-stage--desktop-grid');

        await showDesktopSpread(0, {
          save: false,
          targetPageIndex: 0
        });

        scrollPagedStageIntoView();

        stage.scrollLeft = 0;
        stage.scrollTop = 0;
      } else {
        window.scrollTo({
          top: 0,
          behavior: 'auto'
        });
      }

      requestAnimationFrame(() => {
        update({ save: false });
        isRestoring = false;
      });
    });

    setTimeout(async () => {
      if (isPaged()) {
        fitPagedStage();

        if (shouldOfferResume) {
          await showDesktopSpread(0, {
            save: false,
            targetPageIndex: 0
          });
        } else {
          const savedProgress = getSavedProgress(manifest.handle);
          const pageIndex = clamp(savedProgress?.pageIndex || 0, 0, pages.length - 1);

          await showDesktopSpread(getSpreadIndexFromPageIndex(pageIndex), {
            save: false,
            targetPageIndex: pageIndex
          });
        }

        scrollPagedStageIntoView();

        stage.scrollLeft = 0;
        stage.scrollTop = 0;
      } else if (shouldOfferResume) {
        update({ save: false });
      } else {
        await restoreScrollProgress(manifest, pages);
      }

      requestAnimationFrame(() => {
        update({ save: false });
        isRestoring = false;

        if (endCard && endCard.returnedFromPost) {
          dismissResumePrompt();
          endCard.settleInView();
        }
      });
    }, 0);

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    window.addEventListener('resize', fitPagedStage);
    window.addEventListener('keydown', onKeydown);
    stage.addEventListener('click', onStageClick);
    stage.addEventListener('wheel', onStageWheel, { passive: false });
    stage.addEventListener('pointerdown', onStagePointerDown);
    stage.addEventListener('pointermove', onStagePointerMove);
    stage.addEventListener('pointerup', endDrag);
    stage.addEventListener('pointercancel', endDrag);

    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', syncPinchState);
    }

    const setPhoneLayout = async (layout) => {
      const nextPages = layout === PHONE_LAYOUT_PAGES;
      if (nextPages === phonePages) return;

      phonePages = nextPages;
      savePhoneLayout(layout);
      updateLayoutToggleUI();

      // Desktop is paged either way; the choice takes effect below the desktop width.
      if (desktopSpreadQuery.matches) return;

      isRestoring = true;
      await syncDesktopMode();

      requestAnimationFrame(() => {
        update({ save: false });
        isRestoring = false;
      });
    };

    scrollLayoutButton.addEventListener('click', () => {
      setPhoneLayout(PHONE_LAYOUT_SCROLL);
    });

    pagesLayoutButton.addEventListener('click', () => {
      setPhoneLayout(PHONE_LAYOUT_PAGES);
    });

    updateLayoutToggleUI();

    prevButton.addEventListener('click', () => {
      performDesktopJump(-1);
    });

    nextButton.addEventListener('click', () => {
      performDesktopJump(1);
    });

    zoomOutButton.addEventListener('click', () => {
      if (!desktopSpreadQuery.matches) return;
      setDesktopZoom(desktopZoom - DESKTOP_ZOOM_STEP);
    });

    zoomInButton.addEventListener('click', () => {
      if (!desktopSpreadQuery.matches) return;
      setDesktopZoom(desktopZoom + DESKTOP_ZOOM_STEP);
    });

    singleButton.addEventListener('click', () => {
      setDesktopViewMode(DESKTOP_VIEW_MODE_SINGLE);
    });

    spreadButton.addEventListener('click', () => {
      setDesktopViewMode(DESKTOP_VIEW_MODE_SPREAD);
    });

    gridButton.addEventListener('click', () => {
      setDesktopViewMode(DESKTOP_VIEW_MODE_GRID);
    });

    const onDesktopModeChange = async () => {
      isRestoring = true;
      await syncDesktopMode();

      requestAnimationFrame(() => {
        update({ save: false });
        isRestoring = false;
      });
    };

    if (desktopSpreadQuery.addEventListener) {
      desktopSpreadQuery.addEventListener('change', onDesktopModeChange);
    } else if (desktopSpreadQuery.addListener) {
      desktopSpreadQuery.addListener(onDesktopModeChange);
    }

    update({ save: false });
  }

  document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('.reader-app[data-manifest-id]').forEach(initReader);
  });
})();